import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { ECalendarEventType } from 'src/components/Calendar/Calendar.interface';
import { DEFAULT_POSITION_ALL, ISO_DATE_FORMAT } from 'src/constants';
import { PATHS } from 'src/constants/paths';

import {
  CalendarEvent,
  DefaultAssemblyScheduleItem,
  Position,
} from './EventContentSidebar.interface';

export const getScheduleDetails = (
  selectedEvent: CalendarEvent | undefined,
): (DefaultAssemblyScheduleItem | Position)[] => {
  if (selectedEvent?.eventType === ECalendarEventType.defaultAssembly) {
    return selectedEvent?.scheduleItems ?? [];
  }

  return (selectedEvent?.positions as Position[]) ?? [];
};

export const openSchedulePreview = ({
  locationGroupId,
  daypartId,
  date,
}: {
  locationGroupId: string | number;
  daypartId: string | number;
  date?: string | Date;
}) => {
  const previewDate = date ? dayjs(date).format(ISO_DATE_FORMAT) : undefined;

  const previewPath = PATHS.SCHEDULE_PREVIEW(
    String(locationGroupId),
    undefined,
    String(daypartId),
    previewDate,
  );

  const previewUrl = `${window.location.origin}${window.location.pathname}#${previewPath}`;
  window.open(previewUrl, '_blank');
};

export const getMenuBoardModificationNavigation = ({
  selectedEvent,
  position,
  locationCode,
  channelName,
  locationGroupId,
  daypartId,
}: {
  selectedEvent: CalendarEvent | undefined;
  position?: string;
  locationCode: string;
  channelName: string;
  locationGroupId: string;
  daypartId?: string;
}) => {
  const firstOverridablePositionForMenuBoardUrl =
    selectedEvent?.eventType === ECalendarEventType.scheduleComponent
      ? selectedEvent.positions.find((pos) =>
          pos.scheduleItems.some((item) => item.allowOverrides),
        )?.number
      : undefined;

  const selectedOverridePosition =
    position && position !== DEFAULT_POSITION_ALL
      ? Number(position)
      : firstOverridablePositionForMenuBoardUrl;

  const selectedEventStartDate = dayjs(selectedEvent?.start);
  const menuBoardModificationDate =
    selectedEventStartDate.isValid() &&
    selectedEventStartDate.isAfter(dayjs(), 'day')
      ? selectedEventStartDate.format(ISO_DATE_FORMAT)
      : dayjs().format(ISO_DATE_FORMAT);

  return {
    menuBoardPath: PATHS.MENU_BOARD_MODIFICATION(
      locationCode,
      encodeURIComponent(channelName),
      locationGroupId,
      daypartId,
      menuBoardModificationDate,
      undefined,
      selectedOverridePosition,
    ),
    navigateBackLink: `${PATHS.LOCATION_CALENDAR(
      locationCode,
    )}?groupID=${locationGroupId}`,
  };
};

export const isPreviewEnabled = (
  startDate?: string | Date | null,
  endDate?: string | Date | null,
): boolean => {
  const today = dayjs();
  dayjs.extend(isSameOrAfter);
  // Validate start date: must not be more than 3 years in the future
  if (startDate) {
    const start = dayjs(startDate);
    if (!start.isValid()) return false;

    const threeYearsLater = today.add(3, 'year');
    if (start.isAfter(threeYearsLater, 'day')) {
      return false; // start date too far → disable
    }
  }

  //  If no end date, enable (as long as start date is valid)
  if (!endDate) return true;

  //  Validate end date: must be within past 30 days (inclusive)
  const end = dayjs(endDate);
  if (!end.isValid()) return false;

  const thirtyDaysAgo = today.subtract(30, 'day');

  return end.isSameOrAfter(thirtyDaysAgo, 'day');
};

export const isOnlyItemRemaining = (
  scheduledItemArray: (DefaultAssemblyScheduleItem | Position)[] | undefined,
): boolean => {
  if (!scheduledItemArray || scheduledItemArray.length === 0) return false;

  const totalItems = (scheduledItemArray as Position[]).reduce(
    (sum, pos) => sum + pos.scheduleItems.length,
    0,
  );

  return totalItems === 1;
};
